import _ from 'lodash';
import { InterpolationMethod } from '@/services/calculationRunner.constants';
import {
  CENTER_LINE_COLOR,
  CONTROL_LIMITS_COLOR,
  ONE_SIGMA_COLOR,
  RUN_RULES_MARKER_MAP,
  TWO_SIGMA_COLOR,
} from '@/summaryReport/summaryData.constants';
import { getTemplateInstanceIdFromProps } from '@/tools/models/templateInstance.utilities';
import {
  OVERALL_RUN_RULE,
  RUN_RULES,
  STATISTIC_OPTIONS,
  STATISTIC_OUTPUT_FEATURE_NAMES,
  STATISTIC_THICKNESS_DEFAULT,
  STATISTIC_THICKNESS_DISCRETE,
  TIMESTAMP_DURATION,
} from '@/tools/operationalizeStatisticsSummary/operationalizeStatisticsSummary.constants';
import { getPrimarySecondaryFeatures } from '@/tools/operationalizeStatisticsSummary/operationalizeStatisticsSummary.utilities';
import { addTrendItem } from '@/trendData/trend.actions';
import { withArrivalCommitIsolation } from '@/trendData/trend.utilities';
import { SAMPLE_OPTIONS } from '@/trendData/trendData.constants';
import { getTemplateProps } from '@/trendData/trendTemplate.actions';
import { updateLaneDisplay } from '@/trendData/yAxis.actions';
import { getShortIdentifier } from '@/utilities/shortIdentifier.utilities';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import type { StatisticType } from '@/tools/operationalizeStatisticsSummary/operationalizeStatisticsSummary.constants';
import type { InstanceOutputMappingV1 } from '@seeqdev/api/model/InstanceOutputMappingV1';
import type { TemplateOutputV1 } from '@seeqdev/api/model/TemplateOutputV1';

export interface LaneReservationOptions {
  above?: string;
  below?: string;
}

export interface TrendLayoutAssignment {
  output: InstanceOutputMappingV1;
  lane?: number;
  axisAlign?: string;
  sampleDisplayOption?: string;
  lineWidth?: number;
  color?: string;
  overlay?: number;
  laneReservation?: string;
  orderInLanePriority?: number;
  laneReservationOptions?: LaneReservationOptions;
}

export interface TrendLayoutPlan {
  assignments: TrendLayoutAssignment[];
}

export interface ClassifiedOutputs {
  primaryChartOutputs: InstanceOutputMappingV1[];
  secondaryChartOutputs: InstanceOutputMappingV1[];
  statisticsOutputs: InstanceOutputMappingV1[];
}

export interface RegroupAssignment {
  id: string;
  lane: number;
  axisAlign: string;
  overlay?: number;
  orderInLanePriority?: number;
}

export function buildLayoutPlan(params: {
  model: TemplateOutputV1;
  instanceId: string;
  outputs: InstanceOutputMappingV1[];
  selectedOutputs: string[];
  interpolationMethod?: InterpolationMethod;
  timestampPlacement?: string;
}): TrendLayoutPlan {
  const { model, instanceId, outputs, selectedOutputs, interpolationMethod, timestampPlacement } = params;

  const { primaryChartOutputs, secondaryChartOutputs, statisticsOutputs } = classifyOutputs({
    model,
    outputs,
    selectedOutputs,
  });

  const isDurationTimestamp = timestampPlacement === TIMESTAMP_DURATION;

  const sampleDisplayOption =
    !isDurationTimestamp && interpolationMethod === InterpolationMethod.DISCRETE
      ? SAMPLE_OPTIONS.BAR
      : SAMPLE_OPTIONS.LINE;

  const assignments: TrendLayoutAssignment[] = [];

  const addChartGroup = (chartOutputs: InstanceOutputMappingV1[]) => {
    if (chartOutputs.length === 0) return;
    const { overallRuleViolations, runRules, others } = separateRunRules(model, chartOutputs);

    for (const output of overallRuleViolations) {
      const featureName = getFeatureName(model, output.featureId!);
      const tag = getReservationTag(instanceId, model, output.featureId!);
      assignments.push({
        output,
        color: OVERALL_RULE_VIOLATION_COLOR,
        laneReservation: tag,
        laneReservationOptions: getReservationOptions(tag),
        orderInLanePriority: getOrderInLanePriority(featureName),
      });
    }
    for (const output of runRules) {
      const featureName = getFeatureName(model, output.featureId!);
      const tag = getReservationTag(instanceId, model, output.featureId!);
      assignments.push({
        output,
        color: getRunRuleColor(featureName),
        laneReservation: tag,
        laneReservationOptions: getReservationOptions(tag),
        orderInLanePriority: getOrderInLanePriority(featureName),
      });
    }

    if (others.length > 0) {
      for (const output of others) {
        const featureName = getFeatureName(model, output.featureId!);
        const color = getScalarColor(featureName);
        const tag = getReservationTag(instanceId, model, output.featureId!);
        assignments.push({
          output,
          laneReservation: tag,
          laneReservationOptions: getReservationOptions(tag),
          ...(color !== undefined ? { color } : {}),
        });
      }
    }
  };

  addChartGroup(primaryChartOutputs);
  addChartGroup(secondaryChartOutputs);

  const statisticLineWidth =
    !isDurationTimestamp && interpolationMethod === InterpolationMethod.DISCRETE
      ? STATISTIC_THICKNESS_DISCRETE
      : STATISTIC_THICKNESS_DEFAULT;

  for (const output of statisticsOutputs) {
    const tag = getReservationTag(instanceId, model, output.featureId!);
    assignments.push({
      output,
      sampleDisplayOption,
      lineWidth: statisticLineWidth,
      laneReservation: tag,
      laneReservationOptions: getReservationOptions(tag),
    });
  }

  return { assignments };
}

export async function applyLayoutPlan(plan: TrendLayoutPlan): Promise<void> {
  const validAssignments = plan.assignments.filter((a) => !!a.output.outputId);
  if (validAssignments.length === 0) return;

  // Phase 1: Fetch all item data and model props in parallel — no store mutations
  const items = await Promise.all(
    validAssignments.map(async (assignment) => {
      const { data } = await sqItemsApi.getItemAndAllProperties({ id: assignment.output.outputId! });
      return { assignment, item: data };
    }),
  );

  const uniqueInstanceIds = new Set<string>();
  const modelPropsCache = new Map<string, Partial<Record<string, unknown>>>();
  const validItems = items.filter(({ item }) => !!item?.properties);

  for (const { item } of validItems) {
    const instanceId = getTemplateInstanceIdFromProps(item!.properties);
    if (instanceId) {
      uniqueInstanceIds.add(instanceId);
    }
  }

  await Promise.all(
    [...uniqueInstanceIds].map(async (instanceId) => {
      const props = await getTemplateProps(instanceId);
      modelPropsCache.set(instanceId, props);
    }),
  );

  // Phase 2: Add/refresh all items via addTrendItem — infrastructure resolves lanes from reservations.
  // The arrival-commit isolation window makes the parallel fetches commit their results on separate
  // macrotasks, without which the burst of store writes trips React's nested-update limit (#185).
  await withArrivalCommitIsolation(() =>
    Promise.all(
      validItems.map(async ({ assignment, item }) => {
        const instanceId = getTemplateInstanceIdFromProps(item!.properties);
        if (!instanceId) return;

        const enrichedItem = {
          ...item,
          ...(assignment.color !== undefined ? { color: assignment.color } : {}),
          ...(assignment.laneReservation !== undefined ? { laneReservation: assignment.laneReservation } : {}),
          ...(assignment.orderInLanePriority !== undefined
            ? { orderInLanePriority: assignment.orderInLanePriority }
            : {}),
          ...(assignment.laneReservationOptions !== undefined
            ? { laneReservationOptions: assignment.laneReservationOptions }
            : {}),
        };
        const cachedProps = modelPropsCache.get(instanceId) ?? {};
        const options = {
          ...cachedProps,
          instanceId,
          ...(assignment.sampleDisplayOption ? { sampleDisplayOption: assignment.sampleDisplayOption } : {}),
          ...(assignment.lineWidth !== undefined ? { lineWidth: assignment.lineWidth } : {}),
          ...(assignment.overlay !== undefined ? { overlay: assignment.overlay } : {}),
        };
        await addTrendItem(enrichedItem, options);
      }),
    ),
  );

  updateLaneDisplay();
}

export function buildRegroupPlan(params: {
  model: TemplateOutputV1;
  instanceOutputs: Array<{ outputId?: string; featureId?: string }>;
  primaryOutputIds: string[];
  secondaryOutputIds: string[];
  statisticOutputIds: string[];
  startLane: number;
  startAlignmentIndex: number;
}): RegroupAssignment[] {
  const {
    model,
    instanceOutputs,
    primaryOutputIds,
    secondaryOutputIds,
    statisticOutputIds,
    startLane,
    startAlignmentIndex,
  } = params;

  if (primaryOutputIds.length === 0 && secondaryOutputIds.length === 0 && statisticOutputIds.length === 0) return [];

  const allocator = createLaneAlignmentAllocator(startLane, startAlignmentIndex);

  const buildGroupAssignments = (outputIds: string[]): RegroupAssignment[] => {
    const { overallRuleViolationIds, runRuleIds, otherIds } = separateRunRuleIds(model, outputIds, instanceOutputs);
    const result: RegroupAssignment[] = [];

    if (overallRuleViolationIds.length > 0 || runRuleIds.length > 0) {
      const slot = allocator.next();
      const featureNameForId = (rid: string) => {
        const output = instanceOutputs.find((o) => o.outputId === rid);
        return output ? getFeatureName(model, output.featureId!) : '';
      };
      for (const id of overallRuleViolationIds) {
        result.push({
          id,
          lane: slot.lane,
          axisAlign: slot.axisAlign,
          orderInLanePriority: getOrderInLanePriority(featureNameForId(id)),
        });
      }
      const sortedRunRuleIds = [...runRuleIds].sort(
        (a, b) => getRunRuleNumber(featureNameForId(a)) - getRunRuleNumber(featureNameForId(b)),
      );
      for (const id of sortedRunRuleIds) {
        result.push({
          id,
          lane: slot.lane,
          axisAlign: slot.axisAlign,
          orderInLanePriority: getOrderInLanePriority(featureNameForId(id)),
        });
      }
    }

    if (otherIds.length > 0) {
      const chartSlot = allocator.next();
      for (const id of otherIds) {
        result.push({ id, lane: chartSlot.lane, axisAlign: chartSlot.axisAlign });
      }
    }

    return result;
  };

  const assignments: RegroupAssignment[] = [];

  if (primaryOutputIds.length > 0) {
    assignments.push(...buildGroupAssignments(primaryOutputIds));
  }
  if (secondaryOutputIds.length > 0) {
    assignments.push(...buildGroupAssignments(secondaryOutputIds));
  }

  const sortedStatisticOutputIds = [...statisticOutputIds].sort((a, b) => {
    const outputA = instanceOutputs.find((o) => o.outputId === a);
    const outputB = instanceOutputs.find((o) => o.outputId === b);
    const nameA = outputA ? getFeatureName(model, outputA.featureId!) : '';
    const nameB = outputB ? getFeatureName(model, outputB.featureId!) : '';
    return getStatisticSortIndex(nameA) - getStatisticSortIndex(nameB);
  });

  for (const id of sortedStatisticOutputIds) {
    const slot = allocator.next();
    assignments.push({ id, lane: slot.lane, axisAlign: slot.axisAlign });
  }

  return assignments;
}

export function classifyOutputs(params: {
  model: TemplateOutputV1;
  outputs: InstanceOutputMappingV1[];
  selectedOutputs: string[];
}): ClassifiedOutputs {
  const { model, outputs, selectedOutputs } = params;
  const { primaryFeatureIds, secondaryFeatureIds } = getPrimarySecondaryFeatures(model);

  const selectedSet = new Set(selectedOutputs);
  const controlChartOutputs: InstanceOutputMappingV1[] = [];
  const statisticsOutputs: InstanceOutputMappingV1[] = [];

  for (const output of outputs) {
    if (!output.outputId || output.outputIdInactive) {
      // Skip inactive or outputs without an ID
    } else if (selectedSet.has(output.featureId!)) {
      const featureName = getFeatureName(model, output.featureId!);
      if (isStatisticFeature(featureName)) {
        statisticsOutputs.push(output);
      } else {
        controlChartOutputs.push(output);
      }
    }
  }

  const primaryChartOutputs = controlChartOutputs.filter((o) => primaryFeatureIds.includes(o.featureId!));
  const secondaryChartOutputs = controlChartOutputs.filter((o) => secondaryFeatureIds.includes(o.featureId!));

  statisticsOutputs.sort((a, b) => {
    const nameA = getFeatureName(model, a.featureId!);
    const nameB = getFeatureName(model, b.featureId!);
    return getStatisticSortIndex(nameA) - getStatisticSortIndex(nameB);
  });

  return { primaryChartOutputs, secondaryChartOutputs, statisticsOutputs };
}

export function separateRunRules(
  model: TemplateOutputV1,
  outputs: InstanceOutputMappingV1[],
): {
  overallRuleViolations: InstanceOutputMappingV1[];
  runRules: InstanceOutputMappingV1[];
  others: InstanceOutputMappingV1[];
} {
  const overallRuleViolations: InstanceOutputMappingV1[] = [];
  const runRules: InstanceOutputMappingV1[] = [];
  const others: InstanceOutputMappingV1[] = [];
  for (const output of outputs) {
    const featureName = getFeatureName(model, output.featureId!);
    if (isOverallRuleViolationFeature(featureName)) {
      overallRuleViolations.push(output);
    } else if (isRunRuleFeature(featureName)) {
      runRules.push(output);
    } else {
      others.push(output);
    }
  }
  return { overallRuleViolations, runRules, others };
}

export function separateRunRuleIds(
  model: TemplateOutputV1,
  outputIds: string[],
  instanceOutputs: Array<{ outputId?: string; featureId?: string }>,
): { overallRuleViolationIds: string[]; runRuleIds: string[]; otherIds: string[] } {
  const overallRuleViolationIds: string[] = [];
  const runRuleIds: string[] = [];
  const otherIds: string[] = [];
  for (const outputId of outputIds) {
    const output = instanceOutputs.find((o) => o.outputId === outputId);
    if (output) {
      const featureName = getFeatureName(model, output.featureId!);
      if (isOverallRuleViolationFeature(featureName)) {
        overallRuleViolationIds.push(outputId);
      } else if (isRunRuleFeature(featureName)) {
        runRuleIds.push(outputId);
      } else {
        otherIds.push(outputId);
      }
    }
  }
  return { overallRuleViolationIds, runRuleIds, otherIds };
}

function createLaneAlignmentAllocator(baseLane: number, baseAlignmentIndex: number): LaneAlignmentAllocator {
  let offset = 0;
  return {
    next() {
      const lane = baseLane + offset;
      const axisAlign = _.toUpper(getShortIdentifier(baseAlignmentIndex + offset));
      offset += 1;
      return { lane, axisAlign };
    },
  };
}

interface LaneAlignmentAllocator {
  next(): { lane: number; axisAlign: string };
}

export const getFeatureName = (model: TemplateOutputV1, featureId: string): string => {
  const feature = model.derivedFeatures?.find((f) => f.id === featureId);
  return feature?.name ?? '';
};

/**
 * Canonical order for rule violation outputs.
 * 'Overall' is drawn on top (lowest priority) and then WE rules are drawn in numerical order below in the lane
 * Each entry is a key that appears in the feature name pattern:
 *   - 'Overall' matches `{primary|secondary}ChartOverallRuleViolationOutput`
 *   - 'WE1' matches `{primary|secondary}ChartWE1RuleViolationOutput`
 * To add a new rule type, append it here.
 */
export const RULE_OVERLAY_ORDER = [
  OVERALL_RUN_RULE,
  RUN_RULES.WESTERN_ELECTRIC.WE1,
  RUN_RULES.WESTERN_ELECTRIC.WE2,
  RUN_RULES.WESTERN_ELECTRIC.WE3,
  RUN_RULES.WESTERN_ELECTRIC.WE4,
] as const;

type RuleKey = (typeof RULE_OVERLAY_ORDER)[number];

const RULE_VIOLATION_PATTERN = new RegExp(
  `^(primary|secondary)Chart(${RULE_OVERLAY_ORDER.join('|')})RuleViolationOutput$`,
  'i',
);

const extractRuleKey = (featureName: string): RuleKey | undefined => {
  const match = featureName.match(RULE_VIOLATION_PATTERN);
  if (!match) return undefined;
  return RULE_OVERLAY_ORDER.find((key) => key.toLowerCase() === match[2].toLowerCase());
};

export const isRunRuleFeature = (featureName: string) => {
  const key = extractRuleKey(featureName);
  return key !== undefined && key !== OVERALL_RUN_RULE;
};

export const isOverallRuleViolationFeature = (featureName: string) => {
  return extractRuleKey(featureName) === OVERALL_RUN_RULE;
};

export const OVERALL_RULE_VIOLATION_COLOR = '#4A4578';

export const getRunRuleColor = (featureName: string): string | undefined => {
  const key = extractRuleKey(featureName);
  if (key && key !== OVERALL_RUN_RULE) {
    return RUN_RULES_MARKER_MAP[key as keyof typeof RUN_RULES_MARKER_MAP]?.fillColor;
  }
  return undefined;
};

export const getScalarColor = (featureName: string): string | undefined => {
  if (/CenterlineOutput$/i.test(featureName)) return CENTER_LINE_COLOR;
  if (/UclOutput$|LclOutput$/i.test(featureName)) return CONTROL_LIMITS_COLOR;
  if (/Plus1SigmaOutput$|Minus1SigmaOutput$/i.test(featureName)) return ONE_SIGMA_COLOR;
  if (/Plus2SigmaOutput$|Minus2SigmaOutput$/i.test(featureName)) return TWO_SIGMA_COLOR;
  return undefined;
};

export const getRunRuleNumber = (featureName: string): number => {
  const key = extractRuleKey(featureName);
  if (!key || key === OVERALL_RUN_RULE) return 0;
  return RULE_OVERLAY_ORDER.indexOf(key);
};

const STATISTIC_FEATURE_NAMES = new Set<string>(Object.values(STATISTIC_OUTPUT_FEATURE_NAMES));

export const isStatisticFeature = (featureName: string) => STATISTIC_FEATURE_NAMES.has(featureName);

const STATISTIC_FEATURE_SORT_ORDER: Record<string, number> = {};
STATISTIC_OPTIONS.forEach((option, index) => {
  const featureName = STATISTIC_OUTPUT_FEATURE_NAMES[option.value as StatisticType];
  if (featureName) STATISTIC_FEATURE_SORT_ORDER[featureName] = index;
});

export const getStatisticSortIndex = (featureName: string): number =>
  STATISTIC_FEATURE_SORT_ORDER[featureName] ?? Number.MAX_SAFE_INTEGER;

export function getOrderInLanePriority(featureName: string): number | undefined {
  const key = extractRuleKey(featureName);
  if (key === undefined) return undefined;
  return RULE_OVERLAY_ORDER.indexOf(key) + 1;
}

export function getReservationTag(instanceId: string, model: TemplateOutputV1, featureId: string): string {
  const featureName = getFeatureName(model, featureId);
  const isPrimary = featureName.toLowerCase().startsWith('primary');

  if (isOverallRuleViolationFeature(featureName) || isRunRuleFeature(featureName)) {
    return isPrimary ? `spc:${instanceId}:primaryRunRules` : `spc:${instanceId}:secondaryRunRules`;
  }
  if (isStatisticFeature(featureName)) {
    return `spc:${instanceId}:stat:${featureName}`;
  }
  return isPrimary ? `spc:${instanceId}:primaryChart` : `spc:${instanceId}:secondaryChart`;
}

export function getReservationOptions(tag: string): LaneReservationOptions | undefined {
  if (tag.includes(':primaryRunRules')) {
    const base = tag.replace(':primaryRunRules', '');
    return { above: `${base}:primaryChart` };
  }
  if (tag.includes(':primaryChart')) {
    const base = tag.replace(':primaryChart', '');
    return { below: `${base}:primaryRunRules` };
  }
  if (tag.includes(':secondaryRunRules')) {
    const base = tag.replace(':secondaryRunRules', '');
    return { above: `${base}:secondaryChart` };
  }
  if (tag.includes(':secondaryChart')) {
    const base = tag.replace(':secondaryChart', '');
    return { below: `${base}:secondaryRunRules` };
  }
  return undefined;
}
